import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { getLogtoAccessToken } from '@/lib/logto';

/* ----------------------------------------------------------------- */
/* Types                                                              */
/* ----------------------------------------------------------------- */

export type MeshStatus =
  | 'none'
  | 'queued'
  | 'claimed'
  | 'meshing'
  | 'validating'
  | 'uploading'
  | 'ready'
  | 'failed'
  | 'animating_queued'
  | 'animating_rigging'
  | 'animating_retargeting'
  | 'animating_merging'
  | 'animating_uploading'
  | 'animated_ready'
  | 'animated_failed';

export interface AvatarMesh {
  status: MeshStatus;
  /**
   * Which provider generated this mesh — drives the cross-provider rig
   * compatibility check (a Tripo mesh can't be rigged by Meshy reliably and
   * vice versa). Absent on legacy docs created before Phase 4 (assume tripo).
   * Phase 5 — `rodin-fal` is Rodin Gen-1 via the fal.ai gateway.
   */
  provider?: 'tripo' | 'meshy' | 'mixamo-manual' | 'rodin-fal' | 'text-source' | 'multi-image-source';
  glbUrl?: string;
  posterUrl?: string;
  attempts: number;
  failureReason?: string;
  lastHeartbeatAt?: string;
  completedAt?: string;
  // Phase 2 legacy fields (mirror of active pack — kept for backward compat)
  rigged?: boolean;
  animations?: AvatarAnimationClip[];
  animationsGlbUrl?: string;
  animationsPosterUrl?: string;
  animationsCompletedAt?: string;
  // Phase 3 multi-pack
  animationPacks?: AvatarAnimationPack[];
  activePackId?: string;
  // Phase 5 skin marketplace — re-textured variants of the same mesh.
  // Independent of animationPacks: switching activeSkinId swaps the rendered
  // GLB but doesn't touch rig/animation state.
  skins?: AvatarMeshSkin[];
  activeSkinId?: string;
}

// Phase 7a — 2D animation clips (image-to-video MP4s). Lives on the AVATAR
// (not under mesh) because the path doesn't need a 3D mesh.
export type VideoClipStatus = 'queued' | 'generating' | 'ready' | 'failed';
export type Video2dProviderId =
  | 'hailuo-fal'
  | 'kling-fal'
  | 'pixverse-fal'
  | 'vidu-fal'
  | 'veo31-fal'
  | 'veo31-fast-fal';

/** Phase 7c — see backend avatar.model.ts VideoClipTransparency. */
export type VideoClipTransparency = 'none' | 'chroma-key' | 'matting';

export interface AvatarVideoClip {
  clipId: string;
  label?: string;
  prompt: string;
  provider: Video2dProviderId;
  status: VideoClipStatus;
  videoUrl?: string;
  posterUrl?: string;
  durationSec?: number;
  resolution?: '512p' | '768p' | '1080p';
  costCents?: number;
  /** Phase 7d — Manage sheet: archived clips hide from the carousel + free
   *  a capacity slot. Reversible via PATCH /video-clips/:id. */
  archived?: boolean;
  /** Phase 7e — optional seed forwarded to the provider for reproducible runs. */
  seed?: number;
  /** Phase 7c. */
  transparency?: VideoClipTransparency;
  chromaKeyHex?: string;
  /** Legacy VP9-alpha WebM (Chromium only). */
  webmAlphaUrl?: string;
  /** H.264 dual-stream matting — color channel. */
  matteRgbUrl?: string;
  /** H.264 dual-stream matting — grayscale alpha mask. */
  matteAlphaUrl?: string;
  /**
   * Phase 7g — matting follow-up lifecycle.
   * 'pending' = MP4 ready, dual-stream still being produced by veed/fal.
   * 'ready'   = matteRgbUrl + matteAlphaUrl persisted.
   * 'failed'  = matting job failed, see matteFailureReason; clip stays
   *             usable via chroma-key fallback. Undefined for clips
   *             generated before Phase 7g and for transparency≠'matting'.
   */
  matteStatus?: 'pending' | 'ready' | 'failed';
  matteFailureReason?: string;
  startedAt: string;
  completedAt?: string;
  failureReason?: string;
}

export type VideoClipStatusResponse = AvatarVideoClip;

export type SkinStatus = 'queued' | 'retexturing' | 'ready' | 'failed';

export interface AvatarMeshSkin {
  skinId: string;
  label?: string;
  textPrompt?: string;
  imageStyleUrl?: string;
  provider: 'meshy';
  status: SkinStatus;
  glbUrl?: string;
  posterUrl?: string;
  costCents?: number;
  startedAt: string;
  completedAt?: string;
  failureReason?: string;
}

export interface SkinStatusResponse {
  skinId: string;
  status: SkinStatus;
  label?: string;
  glbUrl?: string;
  costCents?: number;
  failureReason?: string;
  startedAt: string;
  completedAt?: string;
}

// Phase 3 — library identifier. Preset is now a free-form string id
// (e.g. 'idle' or 'meshy-dance-22').
// Phase 6 — added 'meshy-full' (520+ presets, categorized picker UI).
export type AnimationLibrary = 'tripo-utility' | 'meshy-dancing' | 'meshy-full';

export interface AvatarAnimationClip {
  preset: string;
  clipName: string;
  taskId: string;
  status: 'queued' | 'pending' | 'ready' | 'failed';
  durationMs?: number;
  failureReason?: string;
  glbUrl?: string;
}

export interface AvatarAnimationPack {
  packId: string;
  library: AnimationLibrary;
  provider: 'tripo' | 'meshy';
  status: 'queued' | 'rigging' | 'retargeting' | 'merging' | 'uploading' | 'ready' | 'failed';
  rigTaskId?: string;
  rigGlbUrl?: string;
  animations: AvatarAnimationClip[];
  animationsGlbUrl?: string;
  animationsPosterUrl?: string;
  costCents?: number;
  startedAt?: string;
  completedAt?: string;
  failureReason?: string;
}

// Phase 2 → Phase 3 transition: Tripo's 10 utility presets, kept for
// strict callers. New code should consume `LibraryPreset` from the
// /libraries catalog rather than hardcoding this union.
export type AnimationPreset =
  | 'idle' | 'walk' | 'run' | 'jump' | 'climb'
  | 'slash' | 'shoot' | 'hurt' | 'fall' | 'turn';

export interface LibraryCatalogEntry {
  library: AnimationLibrary;
  provider: 'tripo' | 'meshy';
  label: string;
  presetCount: number;
  estimatedMinutes: number;
  estimatedCostUsd: number;
  /** Fixed rig cost (paid once per pack, regardless of preset subset). */
  rigCostUsd: number;
  /** Marginal cost per retarget — drives the live total in the movement picker. */
  perClipCostUsd: number;
  enabled: boolean;
  /** Library catalog presets (id + user-facing label). */
  /**
   * Phase 6 — optional category/subcategory for the categorized picker
   * (only meshy-full populates these; tripo-utility + meshy-dancing leave
   * them undefined and render as flat chip lists).
   */
  presets: Array<{ id: string; label: string; category?: string; subcategory?: string }>;
}

export type AnimationPhase =
  | 'queued'
  | 'rigging'
  | 'retargeting'
  | 'merging'
  | 'uploading'
  | 'ready'
  | 'failed'
  | 'unknown';

export interface AnimationStatusResponse {
  phase: AnimationPhase;
  overallPct: number;
  /** Phase 3 — pack-scoped progress. */
  inFlightPackId?: string;
  inFlightLibrary?: AnimationLibrary;
  totalClips: number;
  animations: Array<{ preset: string; status: 'queued' | 'pending' | 'ready' | 'failed' }>;
  etaSeconds: number;
  failureReason?: string;
}

export interface AvatarItem {
  _id: string;
  userId: string;
  title: string;
  prompt: string;
  imageUrl: string;
  thumbnailUrl: string;
  provider: string;
  model: string;
  style: string;
  aspectRatio: '1:1' | '3:4';
  visibility: 'private' | 'public';
  status: string;
  favoriteCount: number;
  templateId?: string;
  seriesId: string;
  parentAvatarId?: string | null;
  seriesCount?: number;
  hasAlpha?: boolean;
  isFeatured?: boolean;
  /**
   * When this avatar was generated from a user-uploaded reference photo
   * (multipart `image` field on POST /avatars/generate), the original image
   * URL is stored here. Absent for text-prompt-only avatars.
   */
  sourceImageUrl?: string;
  mesh?: AvatarMesh;
  /** Phase 7a — 2D animation clips. activeVideoClipId picks the one auto-played. */
  videoClips?: AvatarVideoClip[];
  activeVideoClipId?: string;
  createdAt: string;
}

export type Upgrade3dStatus = 'queued' | 'already_in_flight' | 'deduped';

export interface Upgrade3dResponse {
  status: Upgrade3dStatus;
  avatar: AvatarItem;
}

export interface AvatarTemplate {
  _id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  isDefault: boolean;
  /**
   * Optional user-facing hint shown under the prompt textarea on the
   * avatar-creation screen. Authored by admins via /console/prompts (Phase 4).
   * Treated as a soft suggestion — no validation enforced client-side.
   */
  guidelines?: string;
  /**
   * Animation providers whose rigging endpoint is known to accept this
   * template's avatars. Set by admins via /console/prompts (Phase 3 closure).
   * `AnimateLibraryModal` filters libraries by this. Missing/empty means
   * permissive (treat as both ['tripo','meshy']) to preserve legacy behavior.
   */
  compatibleProviders?: Array<'tripo' | 'meshy'>;
}

export interface AvatarTemplateDetail extends AvatarTemplate {
  systemPrompt: string;
  model?: string;
  editModel?: string;
  isActive?: boolean;
}

export interface AvatarModelInfo {
  id: string;
  name: string;
  description?: string;
  provider: string;
  isDefault?: boolean;
  deprecated?: boolean;
  deprecatedAt?: string;
}

export type GenerationJobState =
  | 'pending'
  | 'generating'
  | 'processing'
  | 'complete'
  | 'failed';

export interface GenerationJobStatus {
  id: string;
  status: GenerationJobState;
  progress: { step: string; percentage: number };
  error?: string;
  avatar?: AvatarItem;
}

export interface PaginatedAvatars {
  items: AvatarItem[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

export interface GenerateAvatarParams {
  prompt: string;
  title?: string;
  templateId?: string;
  model?: string;
  style?: string;
  aspectRatio?: '1:1' | '3:4';
  visibility?: 'private' | 'public';
  transparent?: boolean;
  /**
   * Optional reference photo (image-to-image). When provided:
   *   - the request switches from JSON to multipart/form-data
   *   - the backend forces a Gemini-family model (only one supporting image input)
   *   - the `prompt` becomes optional (system prompt + photo alone are enough)
   */
  sourceImage?: File | null;
}

/* ----------------------------------------------------------------- */
/* RTK Query                                                          */
/* ----------------------------------------------------------------- */

/**
 * QAvatar API client. Talks to qavatar.qlash.gg (in dev: Vite proxy at
 * `/qavatar-api`). Auth piggybacks on the Logto access token (JWKS-validated
 * server-side). Mirrors the mobile-app/feat/avatars `avatarApi.ts` shape.
 */
export const avatarApi = createApi({
  reducerPath: 'avatarApi',
  tagTypes: ['Avatars', 'MyAvatars', 'AvatarTemplates', 'Favorites', 'AvatarSeries', 'FeaturedAvatar'],
  baseQuery: fetchBaseQuery({
    baseUrl: env.avatarApiUrl,
    prepareHeaders: async (headers) => {
      const token = await getLogtoAccessToken();
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Accept', 'application/json');
      // Note: Content-Type is intentionally not set here. fetchBaseQuery
      // auto-sets `application/json` for plain-object bodies AND lets the
      // browser auto-set `multipart/form-data; boundary=…` for FormData
      // bodies. Setting it manually would break the multipart upload used
      // by photo-base generation (image file + text fields).
      headers.set('platform', 'telegram');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    /* ---- Public ---- */

    getGallery: builder.query<
      PaginatedAvatars,
      { page?: number; limit?: number; templateId?: string } | void
    >({
      query: (params) => ({ url: '/avatars/gallery', params: params ?? undefined }),
      transformResponse: (raw: ApiEnvelope<PaginatedAvatars>) => raw.data,
      providesTags: ['Avatars'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const args = (queryArgs ?? {}) as { page?: number; limit?: number; templateId?: string };
        return `${endpointName}-${JSON.stringify({ limit: args.limit, templateId: args.templateId })}`;
      },
      merge: (currentCache, newResponse, { arg }) => {
        const args = (arg ?? {}) as { page?: number };
        if (!args.page || args.page <= 1) return newResponse;
        currentCache.items.push(...newResponse.items);
        currentCache.page = newResponse.page;
        currentCache.total = newResponse.total;
        currentCache.totalPages = newResponse.totalPages;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const cur = (currentArg ?? {}) as { page?: number };
        const prev = (previousArg ?? {}) as { page?: number };
        return cur.page !== prev.page;
      },
    }),

    getTemplates: builder.query<AvatarTemplate[], void>({
      query: () => ({ url: '/avatars/templates' }),
      transformResponse: (raw: ApiEnvelope<AvatarTemplate[]>) => raw.data,
      providesTags: ['AvatarTemplates'],
    }),

    getTemplateDetail: builder.query<AvatarTemplateDetail, string>({
      query: (id) => ({ url: `/admin/avatars/templates/${id}` }),
      transformResponse: (raw: ApiEnvelope<AvatarTemplateDetail>) => raw.data,
      providesTags: (_r, _e, id) => [{ type: 'AvatarTemplates' as const, id }],
    }),

    getModels: builder.query<AvatarModelInfo[], void>({
      query: () => ({ url: '/avatars/models' }),
      transformResponse: (raw: ApiEnvelope<AvatarModelInfo[]>) => raw.data,
    }),

    getAvatar: builder.query<AvatarItem, string>({
      query: (id) => ({ url: `/avatars/${id}` }),
      transformResponse: (raw: ApiEnvelope<AvatarItem>) => raw.data,
      providesTags: (_r, _e, id) => [{ type: 'Avatars', id }],
    }),

    /* ---- Authenticated ---- */

    getFeaturedAvatar: builder.query<AvatarItem | null, void>({
      query: () => ({ url: '/avatars/featured' }),
      transformResponse: (raw: ApiEnvelope<AvatarItem | null>) => raw.data,
      providesTags: ['FeaturedAvatar'],
    }),

    setFeaturedAvatar: builder.mutation<AvatarItem, string>({
      query: (id) => ({ url: `/avatars/${id}/feature`, method: 'PATCH' }),
      transformResponse: (raw: ApiEnvelope<AvatarItem>) => raw.data,
      invalidatesTags: (_r, _e, id) => ['FeaturedAvatar', 'MyAvatars', { type: 'Avatars', id }],
    }),

    getMyAvatars: builder.query<PaginatedAvatars, { page?: number; limit?: number } | void>({
      query: (params) => ({ url: '/avatars/me', params: params ?? undefined }),
      transformResponse: (raw: ApiEnvelope<PaginatedAvatars>) => raw.data,
      providesTags: ['MyAvatars'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const args = (queryArgs ?? {}) as { page?: number; limit?: number };
        return `${endpointName}-${JSON.stringify({ limit: args.limit })}`;
      },
      merge: (currentCache, newResponse, { arg }) => {
        const args = (arg ?? {}) as { page?: number };
        if (!args.page || args.page <= 1) return newResponse;
        currentCache.items.push(...newResponse.items);
        currentCache.page = newResponse.page;
        currentCache.total = newResponse.total;
        currentCache.totalPages = newResponse.totalPages;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const cur = (currentArg ?? {}) as { page?: number };
        const prev = (previousArg ?? {}) as { page?: number };
        return cur.page !== prev.page;
      },
    }),

    getAvatarSeries: builder.query<AvatarItem[], string>({
      query: (id) => ({ url: `/avatars/${id}/series` }),
      transformResponse: (raw: ApiEnvelope<{ items: AvatarItem[]; total: number }>) =>
        raw.data.items,
      providesTags: (_r, _e, id) => [{ type: 'AvatarSeries', id }],
    }),

    generateAvatar: builder.mutation<{ jobId: string }, GenerateAvatarParams>({
      query: (params) => {
        // Photo-base flow — switch to multipart/form-data so we can upload the
        // reference image alongside the text fields.
        if (params.sourceImage) {
          const fd = new FormData();
          fd.append('image', params.sourceImage);
          if (params.prompt) fd.append('prompt', params.prompt);
          if (params.title) fd.append('title', params.title);
          if (params.templateId) fd.append('templateId', params.templateId);
          if (params.model) fd.append('model', params.model);
          if (params.style) fd.append('style', params.style);
          if (params.aspectRatio) fd.append('aspectRatio', params.aspectRatio);
          if (params.visibility) fd.append('visibility', params.visibility);
          if (typeof params.transparent === 'boolean') {
            fd.append('transparent', String(params.transparent));
          }
          return { url: '/avatars/generate', method: 'POST', body: fd };
        }
        // Text-prompt flow — plain JSON body (existing behavior).
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- strip sourceImage out of the JSON body
        const { sourceImage: _omit, ...body } = params;
        return { url: '/avatars/generate', method: 'POST', body };
      },
      transformResponse: (raw: ApiEnvelope<{ jobId: string }>) => raw.data,
    }),

    editAvatar: builder.mutation<
      { jobId: string },
      { id: string; prompt: string; title?: string; transparent?: boolean; model?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/avatars/${id}/edit`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ jobId: string }>) => raw.data,
      invalidatesTags: (_r, _e, { id }) => ['MyAvatars', 'Avatars', { type: 'AvatarSeries', id }],
    }),

    /* Avatar Assistant — Phase 2 AI prompt helper. `enhance` enriches the
     * character concept; `ideas` returns 2-3 fresh concepts. The backend
     * (qavatar-api POST /avatars/enhance-prompt, Gemini-text) owns the
     * no-art-direction invariant. Stateless → no cache tags. Gated client-side
     * by env.featureAvatarAiEnabled (the endpoint isn't on every qavatar yet). */
    enhancePrompt: builder.mutation<
      { enhanced?: string; suggestions?: string[] },
      { prompt: string; templateId?: string; transparent?: boolean; mode: 'enhance' | 'ideas' }
    >({
      query: (body) => ({ url: '/avatars/enhance-prompt', method: 'POST', body }),
      transformResponse: (raw: ApiEnvelope<{ enhanced?: string; suggestions?: string[] }>) => raw.data,
    }),

    getJobStatus: builder.query<GenerationJobStatus, string>({
      query: (jobId) => ({ url: `/avatars/jobs/${jobId}` }),
      transformResponse: (raw: ApiEnvelope<GenerationJobStatus>) => raw.data,
    }),

    updateAvatar: builder.mutation<
      AvatarItem,
      { id: string; title?: string; visibility?: 'private' | 'public' }
    >({
      query: ({ id, ...body }) => ({ url: `/avatars/${id}`, method: 'PATCH', body }),
      transformResponse: (raw: ApiEnvelope<AvatarItem>) => raw.data,
      invalidatesTags: ['MyAvatars', 'Avatars'],
    }),

    deleteAvatar: builder.mutation<void, string>({
      query: (id) => ({ url: `/avatars/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MyAvatars', 'Avatars'],
    }),

    toggleFavorite: builder.mutation<{ favorited: boolean }, string>({
      query: (id) => ({ url: `/avatars/${id}/favorite`, method: 'POST' }),
      transformResponse: (raw: ApiEnvelope<{ favorited: boolean }>) => raw.data,
      invalidatesTags: ['Favorites'],
    }),

    getFavorites: builder.query<AvatarItem[], void>({
      query: () => ({ url: '/avatars/favorites' }),
      transformResponse: (raw: ApiEnvelope<AvatarItem[]>) => raw.data,
      providesTags: ['Favorites'],
    }),

    /* ---- 3D mesh ---- */

    upgrade3d: builder.mutation<
      Upgrade3dResponse,
      { avatarId: string; force?: boolean; provider?: 'tripo' | 'meshy' | 'rodin-fal' }
    >({
      query: ({ avatarId, force, provider }) => ({
        url: `/avatars/${avatarId}/upgrade-3d`,
        method: 'POST',
        body: {
          ...(force ? { force: true } : {}),
          ...(provider ? { provider } : {}),
        },
      }),
      transformResponse: (raw: ApiEnvelope<Upgrade3dResponse>) => raw.data,
      invalidatesTags: (_r, _e, { avatarId }) => [
        'FeaturedAvatar',
        'MyAvatars',
        { type: 'Avatars', id: avatarId },
      ],
    }),

    meshStatus: builder.query<AvatarMesh, string>({
      query: (id) => ({ url: `/avatars/${id}/mesh-status` }),
      transformResponse: (raw: ApiEnvelope<{ mesh: AvatarMesh }>) => raw.data.mesh,
      providesTags: (_r, _e, id) => [{ type: 'Avatars', id }],
    }),

    /* ---- Phase 2/3: animation (multi-pack) ---- */

    animate3d: builder.mutation<
      { status: 'queued' | 'already_in_flight'; avatar: AvatarItem; packId?: string },
      { avatarId: string; library: AnimationLibrary; force?: boolean; presetIds?: string[] }
    >({
      query: ({ avatarId, library, force, presetIds }) => ({
        url: `/avatars/${avatarId}/animate-3d`,
        method: 'POST',
        body: {
          library,
          ...(force ? { force: true } : {}),
          ...(presetIds && presetIds.length > 0 ? { presetIds } : {}),
        },
      }),
      transformResponse: (raw: ApiEnvelope<{ status: 'queued' | 'already_in_flight'; avatar: AvatarItem; packId?: string }>) =>
        raw.data,
      invalidatesTags: (_r, _e, { avatarId }) => [
        'FeaturedAvatar',
        'MyAvatars',
        { type: 'Avatars', id: avatarId },
      ],
    }),

    animationStatus: builder.query<AnimationStatusResponse, string>({
      query: (id) => ({ url: `/avatars/${id}/animation-status` }),
      transformResponse: (raw: ApiEnvelope<AnimationStatusResponse>) => raw.data,
      providesTags: (_r, _e, id) => [{ type: 'Avatars', id }],
    }),

    setActivePack: builder.mutation<
      { avatar: AvatarItem },
      { avatarId: string; packId: string }
    >({
      query: ({ avatarId, packId }) => ({
        url: `/avatars/${avatarId}/active-pack`,
        method: 'PATCH',
        body: { packId },
      }),
      transformResponse: (raw: ApiEnvelope<{ avatar: AvatarItem }>) => raw.data,
      invalidatesTags: (_r, _e, { avatarId }) => [
        'FeaturedAvatar',
        'MyAvatars',
        { type: 'Avatars', id: avatarId },
      ],
    }),

    getLibraries: builder.query<{ libraries: LibraryCatalogEntry[] }, void>({
      query: () => ({ url: '/libraries' }),
      transformResponse: (raw: ApiEnvelope<{ libraries: LibraryCatalogEntry[] }>) => raw.data,
    }),

    /* Phase 5 — skin marketplace */
    reskin: builder.mutation<
      { skinId: string; status: 'queued' },
      { avatarId: string; textPrompt?: string; imageStyleUrl?: string; label?: string; referenceImage?: File }
    >({
      query: ({ avatarId, textPrompt, imageStyleUrl, label, referenceImage }) => {
        // When the user picked a reference image file, send multipart so the
        // backend uploads it + uses the resulting URL as imageStyleUrl.
        if (referenceImage) {
          const fd = new FormData()
          if (textPrompt) fd.append('textPrompt', textPrompt)
          if (label) fd.append('label', label)
          fd.append('referenceImage', referenceImage)
          return { url: `/avatars/${avatarId}/reskin`, method: 'POST', body: fd }
        }
        return {
          url: `/avatars/${avatarId}/reskin`,
          method: 'POST',
          body: {
            ...(textPrompt ? { textPrompt } : {}),
            ...(imageStyleUrl ? { imageStyleUrl } : {}),
            ...(label ? { label } : {}),
          },
        }
      },
      transformResponse: (raw: ApiEnvelope<{ skinId: string; status: 'queued' }>) => raw.data,
      invalidatesTags: (_r, _e, { avatarId }) => [{ type: 'Avatars', id: avatarId }],
    }),

    /* Phase 5+ — direct 3D-from-text / 3D-from-multi-image creation */
    generate3dFromText: builder.mutation<
      { avatarId: string; mesh: { kind: 'text-source'; status: 'queued' } },
      { textPrompt: string; title?: string; templateId?: string }
    >({
      query: (body) => ({ url: '/avatars/generate-3d-from-text', method: 'POST', body }),
      transformResponse: (raw: ApiEnvelope<{ avatarId: string; mesh: { kind: 'text-source'; status: 'queued' } }>) => raw.data,
      invalidatesTags: ['MyAvatars'],
    }),

    generate3dFromImages: builder.mutation<
      { avatarId: string; mesh: { kind: 'multi-image-source'; status: 'queued' }; sourceImageUrls: string[] },
      { images: File[]; title?: string; templateId?: string }
    >({
      query: ({ images, title, templateId }) => {
        const fd = new FormData()
        images.forEach(f => fd.append('images', f))
        if (title) fd.append('title', title)
        if (templateId) fd.append('templateId', templateId)
        return { url: '/avatars/generate-3d-from-images', method: 'POST', body: fd }
      },
      transformResponse: (raw: ApiEnvelope<{ avatarId: string; mesh: { kind: 'multi-image-source'; status: 'queued' }; sourceImageUrls: string[] }>) => raw.data,
      invalidatesTags: ['MyAvatars'],
    }),

    skinStatus: builder.query<SkinStatusResponse, { avatarId: string; skinId: string }>({
      query: ({ avatarId, skinId }) => ({ url: `/avatars/${avatarId}/skin-status/${skinId}` }),
      transformResponse: (raw: ApiEnvelope<SkinStatusResponse>) => raw.data,
      providesTags: (_r, _e, { avatarId }) => [{ type: 'Avatars', id: avatarId }],
    }),

    setActiveSkin: builder.mutation<
      { activeSkinId: string | null },
      { avatarId: string; skinId: string | null }
    >({
      query: ({ avatarId, skinId }) => ({
        url: `/avatars/${avatarId}/active-skin`,
        method: 'PATCH',
        body: { skinId },
      }),
      transformResponse: (raw: ApiEnvelope<{ activeSkinId: string | null }>) => raw.data,
      invalidatesTags: (_r, _e, { avatarId }) => [
        'FeaturedAvatar',
        'MyAvatars',
        { type: 'Avatars', id: avatarId },
      ],
    }),

    /* Phase 7a — 2D animation (image-to-video). Phase 7b adds variants 1-3. */
    animate2d: builder.mutation<
      { clipId: string; clipIds?: string[]; status: 'queued'; estimatedCostCents: number },
      {
        avatarId: string;
        prompt: string;
        provider?: Video2dProviderId;
        durationSec?: 5 | 6 | 10;
        resolution?: '512p' | '768p' | '1080p';
        label?: string;
        variants?: 1 | 2 | 3;
        transparency?: VideoClipTransparency;
        seed?: number;
      }
    >({
      query: ({ avatarId, ...body }) => ({
        url: `/avatars/${avatarId}/animate-2d`,
        method: 'POST',
        body,
      }),
      transformResponse: (raw: ApiEnvelope<{ clipId: string; clipIds?: string[]; status: 'queued'; estimatedCostCents: number }>) => raw.data,
      // Phase 7c bug: creating a new clip on the featured avatar must also
      // bust the FeaturedAvatar cache, otherwise the Hub keeps showing the
      // previously-active clip until a full reload. Same for MyAvatars
      // (the avatars list shows a Video chip aggregated across clips).
      invalidatesTags: (_r, _e, { avatarId }) => [
        { type: 'Avatars', id: avatarId },
        'FeaturedAvatar',
        'MyAvatars',
      ],
    }),

    videoClipStatus: builder.query<VideoClipStatusResponse, { avatarId: string; clipId: string }>({
      query: ({ avatarId, clipId }) => ({ url: `/avatars/${avatarId}/video-clip-status/${clipId}` }),
      transformResponse: (raw: ApiEnvelope<VideoClipStatusResponse>) => raw.data,
      providesTags: (_r, _e, { avatarId }) => [{ type: 'Avatars', id: avatarId }],
    }),

    setActiveVideoClip: builder.mutation<
      { activeVideoClipId: string | null },
      { avatarId: string; clipId: string | null }
    >({
      query: ({ avatarId, clipId }) => ({
        url: `/avatars/${avatarId}/active-video-clip`,
        method: 'PATCH',
        body: { clipId },
      }),
      transformResponse: (raw: ApiEnvelope<{ activeVideoClipId: string | null }>) => raw.data,
      invalidatesTags: (_r, _e, { avatarId }) => [
        'FeaturedAvatar',
        'MyAvatars',
        { type: 'Avatars', id: avatarId },
      ],
    }),

    /* Phase 7d — Manage sheet: archive toggle + rename label. */
    updateVideoClip: builder.mutation<
      { ok: true },
      { avatarId: string; clipId: string; archived?: boolean; label?: string }
    >({
      query: ({ avatarId, clipId, archived, label }) => ({
        url: `/avatars/${avatarId}/video-clips/${clipId}`,
        method: 'PATCH',
        body: { archived, label },
      }),
      transformResponse: (raw: ApiEnvelope<{ ok: true }>) => raw.data,
      invalidatesTags: (_r, _e, { avatarId }) => [
        'FeaturedAvatar',
        'MyAvatars',
        { type: 'Avatars', id: avatarId },
      ],
    }),

    /* Phase 7d — Hard delete clip + cleanup storage files. */
    deleteVideoClip: builder.mutation<
      { ok: true },
      { avatarId: string; clipId: string }
    >({
      query: ({ avatarId, clipId }) => ({
        url: `/avatars/${avatarId}/video-clips/${clipId}`,
        method: 'DELETE',
      }),
      transformResponse: (raw: ApiEnvelope<{ ok: true }>) => raw.data,
      invalidatesTags: (_r, _e, { avatarId }) => [
        'FeaturedAvatar',
        'MyAvatars',
        { type: 'Avatars', id: avatarId },
      ],
    }),
  }),
});

export const {
  useGetGalleryQuery,
  useGetTemplatesQuery,
  useGetTemplateDetailQuery,
  useGetModelsQuery,
  useGetAvatarQuery,
  useGetFeaturedAvatarQuery,
  useSetFeaturedAvatarMutation,
  useGetMyAvatarsQuery,
  useGetAvatarSeriesQuery,
  useGenerateAvatarMutation,
  useEditAvatarMutation,
  useEnhancePromptMutation,
  useGetJobStatusQuery,
  useUpdateAvatarMutation,
  useDeleteAvatarMutation,
  useToggleFavoriteMutation,
  useGetFavoritesQuery,
  useUpgrade3dMutation,
  useMeshStatusQuery,
  useAnimate3dMutation,
  useAnimationStatusQuery,
  useSetActivePackMutation,
  useGetLibrariesQuery,
  useReskinMutation,
  useSkinStatusQuery,
  useSetActiveSkinMutation,
  useGenerate3dFromTextMutation,
  useGenerate3dFromImagesMutation,
  useAnimate2dMutation,
  useVideoClipStatusQuery,
  useSetActiveVideoClipMutation,
  useUpdateVideoClipMutation,
  useDeleteVideoClipMutation,
} = avatarApi;
